import { type useNavigate } from 'react-router-dom';

import { setAccessToken, setCookie, BackrsUser, handleAutoLogin } from '@backrs_ui/common-ui/dist';

import { type PostHog } from 'posthog-js';

import { clearReturnToken } from './emulation';
import { consumePostLoginRedirect } from './postLoginRedirect';
import { getBackrsToken, isGuestToken } from './tokenHelpers';
import { APP_ROUTE } from '../appRoutes.constants';
import { type useAppDispatch } from '../redux/hooks';
import { setUser } from '../redux/user/user';
import { ENVIRONMENT, ENVIRONMENT_URL } from '../settings';

type AfterLoginProps = Readonly<{
  access_token?: string;
  dispatch: ReturnType<typeof useAppDispatch>;
  navigate?: ReturnType<typeof useNavigate>;
  setErrorMessage: (errorMessage: string) => void;
  destination?: string;
  postHog?: PostHog;
}>;

export const handleAfterLogin = async ({
  access_token,
  dispatch,
  navigate,
  setErrorMessage,
  destination,
  postHog,
}: AfterLoginProps) => {
  if (access_token) {
    // must read before the setAccessToken below overwrites the guest JWT
    const cameFromGuestSession = isGuestToken(getBackrsToken());

    // so we know it was a success, we write access token to local storage
    setAccessToken('backrs_ui_token', access_token);
    setCookie(access_token, ENVIRONMENT);

    // a new identity has no emulation to return to
    clearReturnToken();

    // now we can get the user info from DB
    const user: BackrsUser | null = await handleAutoLogin(access_token, ENVIRONMENT_URL);

    // if the user is null, we know something went wrong with authentication so we show the error
    if (!user) {
      setErrorMessage('Error getting user information');

      return;
    }

    if (cameFromGuestSession && postHog && user.id) {
      // posthog-js can be uninitialized (ad-blocker, slow init); a throw here
      // would abort signup after the token swap has landed in localStorage
      try {
        postHog.alias(user.id.toString());
      } catch (error) {
        console.error('Failed to alias posthog identity', error);
      }
    }

    dispatch(setUser(user));

    if (navigate) {
      navigate(destination ?? consumePostLoginRedirect() ?? APP_ROUTE.HOME);
    }
  }
};
